import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cm from 'classnames';

import { useAppSelector } from 'store/store-hooks';
import { PLAYBACK_RATE_DEFAULT } from 'constants/params';
import { useResizeObserver } from 'hooks/use-resize-observer';
import { Path, useLocation } from 'components/_hooks/use-router';
import { getClipLabel, getClipConfigFlags, selectSbClipPlaybackParams } from 'components/app/_helpers/_app.helper';
import { Clip } from 'components/app/editor/_mediator/editor.slice';
import { speedIsEnabled } from 'components/app/editor/_mediator/editor-clips/editor-clips-linking.helper';

import styles from './_clip-labels.module.scss';

interface Props {
  containerRef: React.RefObject<HTMLDivElement>;
  clipData: Clip;
  className?: string;
}

export const ClipLabels = ({ containerRef, clipData, className }: Props) => {
  const sbClipPlaybackMap = useAppSelector((state) => state.sb.sbClipPlaybackMap);
  const fcClipRates = useAppSelector((state) => state.fc__data.fcClipRates);
  const linkedClips = useAppSelector((state) => state.editor.linkedClips);
  const hiddenConfigFlagsRef = useRef<HTMLDivElement>(null);
  const visibleFlagsRef = useRef<HTMLDivElement>(null);
  const { path } = useLocation();

  const [visibleFlags, setVisibleFlags] = useState<string[]>([]);

  const _className = cm(styles.ClipLabels, className);

  const configFlags = useMemo(() => {
    const flags = [...getClipConfigFlags(clipData.config)];
    if (clipData.clipGroupId) flags.push(`group_${clipData.clipGroupId}`);
    return flags;
  }, [clipData]);

  const rate = useMemo(() => {
    switch (path) {
      case Path.selector:
        return clipData.config.speed !== PLAYBACK_RATE_DEFAULT && speedIsEnabled(clipData, linkedClips)
          ? clipData.config.speed
          : undefined;
      case Path.storyboard:
        return sbClipPlaybackMap
          ? selectSbClipPlaybackParams(clipData.clipId, sbClipPlaybackMap, false).rate
          : undefined;
      case Path.finalcuts:
        return fcClipRates ? fcClipRates[clipData.clipId] : undefined;
      default:
        return undefined;
    }
  }, [sbClipPlaybackMap, fcClipRates, clipData, path, linkedClips]);

  const checkConfigFlagsOverflow = useCallback(() => {
    const containerHeight = containerRef.current?.parentElement?.clientHeight || 0;
    const hiddenConfigFlagsHeight = hiddenConfigFlagsRef.current?.clientHeight || 0;
    setVisibleFlags(
      hiddenConfigFlagsHeight > containerHeight ? configFlags.slice(0, configFlags.length - 1) : configFlags
    );
  }, [configFlags]);

  const checkVisibleFlagsOverflow = () => {
    const containerHeight = containerRef.current?.parentElement?.clientHeight || 0;
    if (!visibleFlagsRef.current) return;
    const computedStyleTop = parseInt(window.getComputedStyle(visibleFlagsRef.current).top);
    const visibleFlagsHeight = visibleFlagsRef.current?.clientHeight + computedStyleTop || 0;
    if (visibleFlagsHeight > containerHeight && visibleFlags.length) {
      setVisibleFlags((prevFlags) => prevFlags.slice(0, prevFlags.length - 1));
    }
  };

  useResizeObserver(hiddenConfigFlagsRef, checkConfigFlagsOverflow);

  useEffect(() => {
    checkConfigFlagsOverflow();
  }, [configFlags]);

  useEffect(() => {
    checkVisibleFlagsOverflow();
  }, [visibleFlags]);

  const getLabels = (flags: string[]) => (
    <>
      <div
        className={cm(styles.ClipLabels__time, {
          [styles['ClipLabels__time--start']]: clipData.startTime !== null,
          [styles['ClipLabels__time--end']]: clipData.endTime !== null,
        })}
      >
        {getClipLabel(clipData)}
      </div>
      {!!rate && <span>{rate + 'x'}</span>}
      {flags.map((key) => (
        <span key={key}>{key}</span>
      ))}
    </>
  );

  return (
    <>
      <div ref={visibleFlagsRef} className={_className}>
        {getLabels(configFlags)}
        {visibleFlags.length !== configFlags.length && <span>...</span>}
      </div>
      <div ref={hiddenConfigFlagsRef} className={_className} style={{ opacity: 0 }}>
        {getLabels(configFlags)}
      </div>
    </>
  );
};
